import {
	Component,
	EventEmitter,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppTagConfiguratorComponent } from '@datachain/ui-sdk/components/app-tag-configurator';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { OrderedSet } from 'immutable';
import {
	concat,
	debounceTime,
	filter,
	merge,
	Observable,
	of,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { configuration } from '../../../core/configuration';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import {
	ExpositionDiagnosticError,
	ExpositionErrorKey,
} from '../../domain/exposition-diagnostic-errors.entity';
import { ExpositionProvider } from '../../providers/exposition.provider';
import {
	ExpositionInternalMetadataForm,
	InternalMetadataFormControls,
} from './exposition-internal-metadata.form';
import { ExpositionMetadataEntity } from './exposition-metadata.entity';

@Component({
	selector: 'app-exposition-internal-metadata-config',
	templateUrl: './exposition-internal-metadata.config.component.html',
	styleUrls: ['./exposition-internal-metadata.config.component.scss'],
	providers: [ExpositionInternalMetadataForm],
	inputs: ['expositionMetadata', 'availableTags', 'isInViewMode'],
})
export class ExpositionInternalMetadataConfigComponent extends DcBaseComponent {
	public Appearance: MatFormFieldAppearance = 'legacy';

	@ViewChild(AppTagConfiguratorComponent)
	public tagConfiguratorComponentCmp: AppTagConfiguratorComponent | null = null;

	@Output()
	public hasInternalMetadataChanged = new EventEmitter<boolean>();

	public vo$: Observable<{
		expositionMetadata: ExpositionMetadataEntity;
		availableTags: OrderedSet<AppTagItemEntity>;
		isInViewMode: boolean;
	}>;

	protected readonly OwnMetadataFormControls = InternalMetadataFormControls;
	protected readonly ValidationErrorKeys = ValidationErrorKeys;
	protected readonly Configuration = configuration;

	public constructor(
		public readonly internalMetadataForm: ExpositionInternalMetadataForm,
		@Optional()
		private readonly expositionProvider: ExpositionProvider
	) {
		super();
		this.cmpId = 'exposition-metadata-config';
		this.toObservable<ExpositionMetadataEntity>('expositionMetadata')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((config) => {
					this.internalMetadataForm.populate(config);
				})
			)
			.subscribe();

		concat(
			of(this.internalMetadataForm.form.value),
			this.internalMetadataForm.form.valueChanges.pipe(debounceTime(300))
		)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<boolean>('isInViewMode')),
				filter(([_, isInViewMode]) => !isInViewMode),
				tap(() => {
					if (!this.internalMetadataForm.form.valid) {
						const hasRequiredError = this.internalMetadataForm.form
							.get(InternalMetadataFormControls.Label)
							?.hasError(ValidationErrorKeys.Required);

						if (hasRequiredError) {
							this.expositionProvider.addError(
								ExpositionDiagnosticError.build({
									key: ExpositionErrorKey.ExpositionMetadataMissingFields,
									type: 'critic',
									message: $localize`:@@exposition.diagnosticError.expositionMetadataMissingFields:`,
								})
							);
							this.expositionProvider.removeError(
								ExpositionErrorKey.ExpositionMetadataNotValid
							);
						} else {
							this.expositionProvider.addError(
								ExpositionDiagnosticError.build({
									key: ExpositionErrorKey.ExpositionMetadataNotValid,
									type: 'critic',
									message: $localize`:@@exposition.diagnosticError.expositionMetadataNotValid:`,
								})
							);
							this.expositionProvider.removeError(
								ExpositionErrorKey.ExpositionMetadataMissingFields
							);
						}
					} else {
						this.expositionProvider.removeError(
							ExpositionErrorKey.ExpositionMetadataMissingFields
						);
						this.expositionProvider.removeError(
							ExpositionErrorKey.ExpositionMetadataNotValid
						);
					}
					this.hasInternalMetadataChanged.emit();
				})
			)
			.subscribe();

		this.vo$ = combineAll({
			expositionMetadata: merge(
				of(ExpositionMetadataEntity.build({})),
				this.toObservable<ExpositionMetadataEntity>('expositionMetadata')
			),
			availableTags: merge(
				of(OrderedSet([])),
				this.toObservable<OrderedSet<AppTagItemEntity>>('availableTags')
			),
			isInViewMode: merge(
				of(false),
				this.toObservable<boolean>('isInViewMode')
			),
		});
	}

	public async extractFormData(): Promise<ExpositionMetadataEntity> {
		let tags = new Array<AppTagItemEntity>();
		if (this.tagConfiguratorComponentCmp) {
			tags = await this.tagConfiguratorComponentCmp.getUsedTags();
		}
		const ownMetadata = this.internalMetadataForm.extract();
		return ExpositionMetadataEntity.build({
			id: ownMetadata?.id,
			label: ownMetadata?.label,
			description: ownMetadata?.description,
			tags: OrderedSet(tags),
		});
	}
}
